import { DrawMode } from "@mapbox/mapbox-gl-draw";
import IconEdit from "@mui/icons-material/Edit";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { GqlFeatureType } from "~/__generated__";
import { MapButton } from "~/modules/map/components/MapButton";

import MapEditorFeatureCreateDialog from "../MapEditorFeatureCreateDialog";
import { FeatureCreateOption, FeatureGeometryType } from "../MapEditorFeatureCreateDialog/components/types";

type Props = {
    currentDrawMode?: DrawMode;
    onDrawModeChange: (drawMode: DrawMode) => void;
    setSelectedOption: (option: FeatureCreateOption) => void;
    selectedOption: FeatureCreateOption;
    allowedDrawTypes?: Array<"area" | "observation" | "ecologicalFunction" | "other">;
};

const DRAW_MODE_DEFAULT = "simple_select";

const objectTypeToDrawModeMap: Record<FeatureGeometryType | GqlFeatureType, DrawMode> = {
    LineString: "draw_line_string",
    Point: "draw_point",
    Polygon: "draw_polygon",
};

export default function MapEditorFeatureCreateButton({
    selectedOption,
    setSelectedOption,
    currentDrawMode,
    onDrawModeChange,
    allowedDrawTypes = ["area", "observation", "ecologicalFunction", "other"],
}: Props) {
    const { t } = useTranslation("mapEditor");
    const [showDialog, setShowDialog] = useState<boolean>(false);

    const active = currentDrawMode !== DRAW_MODE_DEFAULT;
    function onDrawButtonClicked() {
        if (active) {
            onDrawModeChange(DRAW_MODE_DEFAULT);
        } else if (selectedOption.type === "area" || selectedOption.type === "other") {
            onDrawModeChange(objectTypeToDrawModeMap[selectedOption.geometryType]);
        } else if (selectedOption.type === "observation") {
            onDrawModeChange(objectTypeToDrawModeMap[selectedOption.observationType.featureType]);
        } else if (selectedOption.type === "ecologicalFunction") {
            onDrawModeChange(objectTypeToDrawModeMap[selectedOption.ecologicalFunctionType.featureType]);
        }
    }

    function onConfirmDialog(option: FeatureCreateOption) {
        setSelectedOption(option);

        if (option.type === "area" || option.type === "other") {
            onDrawModeChange(objectTypeToDrawModeMap[option.geometryType]);
        } else if (option.type === "observation") {
            onDrawModeChange(objectTypeToDrawModeMap[option.observationType.featureType]);
        } else if (option.type === "ecologicalFunction") {
            onDrawModeChange(objectTypeToDrawModeMap[option.ecologicalFunctionType.featureType]);
        }
    }

    function getDrawLabel(): string {
        switch (selectedOption.type) {
            case "observation":
                return t("mapEditorFeatureCreateButton.labelDrawObservation", {
                    observationType: selectedOption.observationType.name,
                });
            case "area":
                return t("mapEditorFeatureCreateButton.labelDrawPlannerArea");
            case "ecologicalFunction":
                return t("mapEditorFeatureCreateButton.labelDrawEcologicalFunction", {
                    ecologicalFunctionType: selectedOption.ecologicalFunctionType.name,
                });
            case "other":
                return t("mapEditorFeatureCreateButton.labelDrawOtherObject");
            case "none":
                return "";
        }
    }

    return (
        <>
            <MapEditorFeatureCreateDialog
                initialOption={selectedOption}
                onConfirm={onConfirmDialog}
                open={showDialog}
                onClose={() => setShowDialog(false)}
                allowedDrawTypes={allowedDrawTypes}
            />
            {selectedOption.type === "none" ? (
                <MapButton
                    icon={<IconEdit />}
                    label={t("mapEditorFeatureCreateButton.labelDrawSomething")}
                    onClick={() => setShowDialog(true)}
                />
            ) : (
                <>
                    <MapButton active={active} icon={<IconEdit />} onClick={onDrawButtonClicked} label={getDrawLabel()} />
                    <MapButton label={t("mapEditorFeatureCreateButton.btnChange")} onClick={() => setShowDialog(true)} />
                </>
            )}
        </>
    );
}
